import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiPost } from "@/lib/api-client";
import { asPercent } from "@/lib/format";
import { asRecord } from "@/lib/type-utils";

interface WorkflowResult {
  probability: number;
  confidence: number;
  direction: string;
  edge: number;
  kelly_fraction: number;
  total_cost_usd: number;
  agent_calls: AgentCallResult[];
  workflow_graph: WorkflowGraph;
  mode: string;
  question: string;
}

interface AgentCallResult {
  agent_role: string;
  model_used: string;
  probability: number;
  confidence: number;
  reasoning: string;
  latency_ms: number;
  cost_usd: number;
  requests_made: { to: string; reason: string }[];
}

interface WorkflowGraph {
  nodes: { id: string; name: string; probability: number; confidence: number; cost_usd: number; latency_ms: number }[];
  edges: { source: string; target: string }[];
  total_cost: number;
  total_agents: number;
}

const MODE_CONFIG = {
  quick: { label: "Quick", description: "Free passthrough", cost: "$0.00", latency: "<1s", agents: 0,
    classes: { active: "border-green-500 bg-green-500/10", title: "text-green-400" } },
  standard: { label: "Standard", description: "3 agents + Architect synthesis", cost: "~$0.15", latency: "~30s", agents: 4,
    classes: { active: "border-yellow-500 bg-yellow-500/10", title: "text-yellow-400" } },
  deep: { label: "Deep", description: "Dynamic workflow — agents request agents", cost: "~$1-3", latency: "~3min", agents: 7,
    classes: { active: "border-purple-500 bg-purple-500/10", title: "text-purple-400" } },
} as const;

type AnalysisMode = keyof typeof MODE_CONFIG;

const AGENT_COLORS: Record<string, string> = {
  specialist: "text-blue-400", coder: "text-cyan-400", qa: "text-orange-400",
  security: "text-red-400", librarian: "text-emerald-400", architect: "text-gold-400", scout: "text-violet-400",
};

export function WorkflowAnalysisPage() {
  const [mode, setMode] = useState<AnalysisMode>("standard");
  const [question, setQuestion] = useState("");
  const [currentPrice, setCurrentPrice] = useState("0.50");
  const [result, setResult] = useState<WorkflowResult | null>(null);

  const analysisMutation = useMutation({
    mutationFn: async () => {
      const parsedPrice = parseFloat(currentPrice);
      if (isNaN(parsedPrice) || parsedPrice < 0 || parsedPrice > 1) {
        throw new Error("Current price must be a valid number between 0 and 1");
      }
      const resp = await apiPost<{ success: boolean; data: WorkflowResult }>("/api/workflow/analyze", {
        question, current_price: parsedPrice, mode,
      });
      return asRecord(resp).data as WorkflowResult;
    },
    onMutate: () => {
      // Clear previous error and result when starting a new analysis
      setResult(null);
    },
    onSuccess: (data) => setResult(data),
  });

  const statsQuery = useQuery({ queryKey: ["workflow-stats"], queryFn: () => apiGet<unknown>("/api/workflow/stats") });

  const directionTone = (dir: string) => dir === "BUY" ? "success" : dir === "SELL" ? "danger" : "default";

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel title="AI Workflow Analysis" description="Dynamic multi-agent prediction market analysis">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(["quick", "standard", "deep"] as const).map((m) => {
            const cfg = MODE_CONFIG[m];
            return (
              <button key={m} type="button" onClick={() => setMode(m)}
                className={`rounded-xl border-2 p-4 text-left transition-all ${mode === m ? cfg.classes.active : "border-gunmetal bg-gunmetal/30 hover:bg-gunmetal/50"}`}>
                <div className={`text-sm font-semibold ${mode === m ? cfg.classes.title : ""}`}>{cfg.label}</div>
                <div className="mt-1 text-xs text-slate-400">{cfg.description}</div>
                <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                  <span>{cfg.cost}</span><span>•</span><span>{cfg.latency}</span><span>•</span><span>{cfg.agents} agents</span>
                </div>
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel title="Market Analysis" description={`Run ${MODE_CONFIG[mode].label} analysis`}
        actions={<Button onClick={() => analysisMutation.mutate()}
          disabled={analysisMutation.isPending || !question.trim()}>
          {analysisMutation.isPending ? "Analyzing..." : "Run Analysis"}</Button>}>
        <div className="space-y-4">
          <div>
            <Label>Market Question</Label>
            <Textarea className="min-h-[80px] w-full" value={question}
              onChange={(e) => setQuestion(e.target.value)} placeholder="Will Bitcoin exceed $100K by end of 2026?" />
          </div>
          <div>
            <Label>Current Market Price (0-1)</Label>
            <Input className="w-40" type="number" step="0.01" min="0" max="1"
              value={currentPrice} onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  setCurrentPrice("");
                } else {
                  const num = parseFloat(value);
                  if (!isNaN(num) && num >= 0 && num <= 1) {
                    setCurrentPrice(value);
                  }
                }
              }}
              title="Current market price between 0 and 1" placeholder="0.50" />
          </div>
        </div>
      </Panel>

      {analysisMutation.isError && !analysisMutation.isPending && (
        <Panel title="Error"><div className="text-red-400 text-sm">
          {analysisMutation.error instanceof Error ? analysisMutation.error.message : "Analysis failed."}</div></Panel>
      )}

      {result && (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <StatCard label="Probability" value={asPercent(result.probability)} />
            <StatCard label="Confidence" value={asPercent(result.confidence)} />
            <StatCard label="Direction" value={result.direction} tone={directionTone(result.direction)} />
            <StatCard label="Edge" value={asPercent(result.edge)} tone={result.edge > 0.05 ? "success" : "default"} />
            <StatCard label="Kelly" value={asPercent(result.kelly_fraction)} hint={`Cost: $${result.total_cost_usd.toFixed(3)}`} />
          </div>

          <Panel title="Agent Analysis" description={`${result.agent_calls.length} agents executed`}>
            <div className="space-y-3">
              {result.agent_calls.map((agent, i) => (
                <div key={i} className="rounded-lg border border-gunmetal bg-gunmetal/30 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-semibold uppercase ${AGENT_COLORS[agent.agent_role] ?? "text-slate-300"}`}>{agent.agent_role}</span>
                      <span className="text-xs text-slate-500">{agent.model_used}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span>{agent.latency_ms}ms</span><span>${agent.cost_usd.toFixed(4)}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-4">
                    <div className="text-sm"><span className="text-slate-400">P: </span><span className="font-medium text-gold-400">{asPercent(agent.probability)}</span></div>
                    <div className="text-sm"><span className="text-slate-400">Conf: </span><span className="font-medium">{asPercent(agent.confidence)}</span></div>
                    {agent.requests_made.length > 0 && <div className="text-xs text-violet-400">Requested: {agent.requests_made.map((r) => r.to).join(", ")}</div>}
                  </div>
                  <div className="mt-2 text-xs text-slate-400 line-clamp-3">{agent.reasoning}</div>
                </div>
              ))}
            </div>
          </Panel>

          {result.workflow_graph?.nodes?.length > 0 && (
            <Panel title="Workflow Graph" description="Agent request chain">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {result.workflow_graph.nodes.map((node) => (
                  <div key={node.id} className="rounded-lg bg-gunmetal/50 p-3 text-center">
                    <div className={`text-xs font-semibold uppercase ${AGENT_COLORS[node.id] ?? "text-slate-300"}`}>{node.name}</div>
                    <div className="mt-1 text-lg font-bold text-gold-400">{asPercent(node.probability)}</div>
                    <div className="text-xs text-slate-400">Conf: {asPercent(node.confidence)}</div>
                    <div className="text-xs text-slate-500">${node.cost_usd.toFixed(4)} · {node.latency_ms}ms</div>
                  </div>
                ))}
              </div>
              {result.workflow_graph.edges.length > 0 && (
                <div className="mt-4 space-y-1">
                  <div className="text-xs font-medium text-slate-400">Request Chain</div>
                  {result.workflow_graph.edges.map((edge, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
                      <span className={`uppercase ${AGENT_COLORS[edge.source] ?? ""}`}>{edge.source}</span>
                      <span className="text-gold-400">&rarr;</span>
                      <span className={`uppercase ${AGENT_COLORS[edge.target] ?? ""}`}>{edge.target}</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          )}

          <Panel title="Raw Response"><JsonView value={result} /></Panel>
        </>
      )}

      <Panel title="Prompt Library Stats"><JsonView value={statsQuery.data} /></Panel>
    </div>
  );
}
